import React from "react";
import { CircleQuestionMark } from "lucide-react";
import helpers from "../../../shared/lib/helpers";
import { useItemForm } from "../model/ItemFormContext";
import CompareItemSearchModal from "./CompareItemSearchModal";
import MyCompareItemIdByUrlModal from "./MyCompareItemIdByUrlModal";
import CompareItemPriceSnapshot, {
  type CompareItemPriceSnapshotData,
} from "./CompareItemPriceSnapshot";
import { resolveAdjustConfig } from "../../user-settings/model/adjustConfigResolver";
import { useAuth } from "../../auth/AuthContext";
import { canShowInhouseFeatures } from "../../../shared/lib/inhouseFeatures";
import type { UserConfigInputs } from "../../user-settings/model/user-config.types";
import { lockedShippingSelection } from "../model/compareShipping";
import { getDirectMyItemUrl, isDirectCompareItemUrl } from "./directCompareUrl";
import {
  buildInitialDirectSpecifiedItems,
  parseSpecifiedCompareItems,
} from "./directSpecifiedItems";
import { buildInitialDirectMyCompareItem } from "./directMyCompareItem";

const formatSpecifiedPrice = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return "-";
  }

  return `${helpers.formatNumber(value)}원`;
};

interface ItemComparisonFieldsProps {
  shop?: { market_site: string; url?: string } | null;
  isPremium: boolean;
  isSmartstore: boolean;
  maxTargetRank: number;
  userAdjustConfig: { adjust_no_exposed: UserConfigInputs["adjust_no_exposed"] } | null;
  item: {
    compare_items_price_snapshot?: CompareItemPriceSnapshotData;
  } | null;
}

const ItemComparisonFields = ({
  isPremium, isSmartstore, maxTargetRank, userAdjustConfig, item, shop,
}: ItemComparisonFieldsProps) => {
  const auth = useAuth();
  const specifiedItemLimit = auth.subscriptionStatus?.specified_item_limit ?? 3;
  const { register, watch, trigger, clearErrors, errors, setValue, getValues } = useItemForm();
  const [compareItemModalShow, setCompareItemModalShow] = React.useState(false);
  const [myCompareItemIdByUrlModalShow, setMyCompareItemIdByUrlModalShow] = React.useState(false);
  const [showGapPriceBasisHelp, setShowGapPriceBasisHelp] = React.useState(false);
  const compareSite = watch("compare_site");
  const compareUrl = watch("compare_url");
  const includeShippingFee = watch("include_shipping_fee");
  const lockedIncludeShipping = lockedShippingSelection(compareSite, compareUrl || "");
  const tryAssignMyCompareItem = watch("extra_config.try_assign_my_compare_item");
  const myItemIdFieldName = compareSite === "direct"
    ? "my_direct_compare_item_id" as const
    : "my_compare_item_id" as const;
  const myItemFieldName = compareSite === "direct"
    ? "my_direct_compare_item" as const
    : "my_compare_item" as const;
  const myCompareItemId = watch(myItemIdFieldName);
  const myCompareItem = watch(myItemFieldName) as { memo?: string } | undefined;
  const compareRange = watch("compare_range");
  const compareItemsSpecified = watch(
    compareSite === "direct" ? "compare_items_direct_specified" : "compare_items_specified",
  );
  const specifiedItems = React.useMemo(
    () => parseSpecifiedCompareItems(compareItemsSpecified),
    [compareItemsSpecified],
  );
  const isSameMarketCompareRangeDisabled = isSmartstore && compareSite === "danawa";
  const canTryAssignMyCompareItem = compareSite === "danawa" || compareSite === "enuri" || (
    compareSite === "naver" && /^https:\/\/search\.shopping\.naver\.com\/catalog\/\d+\/?(?:[?#]|$)/.test(compareUrl || "")
  );
  const comparePriceSnapshot = item?.compare_items_price_snapshot;

  React.useEffect(() => {
    trigger(myItemIdFieldName);
  }, [canTryAssignMyCompareItem, myItemIdFieldName, tryAssignMyCompareItem, trigger]);

  React.useEffect(() => {
    if (lockedIncludeShipping !== null) {
      setValue("include_shipping_fee", lockedIncludeShipping);
    }
  }, [lockedIncludeShipping, setValue]);

  return (
    <>
          <fieldset className="border-0 p-0 m-0 mt-3">
            <legend className="float-none w-auto fs-6 fw-semibold mb-3 px-0">비교 설정</legend>
            <ul className="list-unstyled">
            {/* 비교 사이트, URL, 컬럼 */}
            <li className="mb-3">
              <label className="form-label">가격비교 URL</label>
              <div className="row mb-3">
                <div className="col mx-0  pe-0">
                  <select
                    id="id_compare_site"
                    data-testid="id_compare_site"
                    className={`form-select ${errors.compare_site ? "is-invalid" : ""}`}
                    {...register("compare_site", {
                      required: "가격비교 사이트 선택하세요.",
                      onChange: (event) => {
                        const isDirect = event.target.value === "direct";
                        setValue("include_shipping_fee", false);
                        if (isDirect) {
                          clearErrors("compare_url");
                        }
                        if (isDirect) {
                          const extraConfig = getValues("extra_config") || {};
                          if (!extraConfig.direct_my_item_initialized) {
                            const initializedMyItem = buildInitialDirectMyCompareItem({
                              portalItemId: getValues("my_compare_item_id"),
                              portalItem: getValues("my_compare_item"),
                              directItemId: getValues("my_direct_compare_item_id"),
                              directItem: getValues("my_direct_compare_item"),
                              fallbackUrl: getDirectMyItemUrl(
                                shop,
                                String(getValues("shop_item_id") || ""),
                              ),
                              itemName: String(getValues("name") || ""),
                            });
                            setValue("my_direct_compare_item_id", initializedMyItem.id, { shouldValidate: true });
                            setValue("my_direct_compare_item", initializedMyItem.item);
                            setValue("extra_config", {
                              ...extraConfig,
                              direct_my_item_initialized: true,
                            });
                          }
                          const initializedExtraConfig = getValues("extra_config") || {};
                          if (!initializedExtraConfig.direct_specified_initialized) {
                            const initializedItems = buildInitialDirectSpecifiedItems(
                              getValues("compare_items_specified"),
                              getValues("compare_items_direct_specified"),
                            );
                            setValue("compare_items_direct_specified", initializedItems, { shouldValidate: true });
                            setValue("extra_config", {
                              ...initializedExtraConfig,
                              direct_specified_initialized: true,
                            });
                          }
                          setValue("compare_range", "items_specified");
                          setValue("compare_items_searched", {});
                        } else if (compareSite !== "direct") {
                          setValue("my_compare_item_id", "", { shouldValidate: true });
                          setValue("my_compare_item", {});
                        }
                      },
                    })}
                  >
                    <option value="danawa">다나와</option>
                    <option value="enuri">에누리</option>
                    <option value="naver">네이버</option>
                    <option value="direct" disabled={!isPremium}>
                      직접비교 {!isPremium && "(프로 전용)"}
                    </option>
                  </select>
                  {errors.compare_site && (
                    <div className="invalid-feedback">
                      {errors.compare_site.message}
                    </div>
                  )}
                </div>
                {compareSite == "danawa" && (
                  <div className="col-auto mx-0 pe-0">
                    <select
                      id="id_compare_column"
                      className={`form-select ${errors.compare_column ? "is-invalid" : ""}`}
                      {...register("compare_column", {
                        required: "비교할 컬럼을 선택하세요.",
                      })}
                    >
                      <option value="left">왼쪽 컬럼</option>
                      <option value="right">오른쪽 컬럼</option>
                      <option value="all">양쪽 컬럼</option>
                    </select>
                  </div>
                )}
                {compareSite !== "direct" && <div className="col-auto mx-0">
                  <select
                    id="id_compare_pages"
                    className={`form-select ${errors.compare_pages ? "is-invalid" : ""}`}
                    {...register("compare_pages", {
                      required: "가격비교 페이지 수를 선택하세요.",
                    })}
                  >
                    <option value="1">1 페이지</option>
                    <option value="2">2 페이지</option>
                  </select>
                </div>}
              </div>
              <div className="row">
                <div className="col">
                  <input
                    type="text"
                    id="id_compare_url"
                    data-testid="id_compare_url"
                    className={`form-control ${errors.compare_url ? "is-invalid" : ""}`}
                    {...register("compare_url", {
                      required: compareSite === "direct" ? false : "가격비교 URL 입력하세요.",
                      maxLength: { value: 2048, message: "가격비교 URL은 2048자 이하여야 합니다." },
                      onBlur: () => {
                        void trigger("compare_url");
                      },
                      validate: (value) => {
                        const currentCompareSite = getValues("compare_site");
                        if (currentCompareSite === "direct") return true;
                        if (
                          currentCompareSite === "naver" &&
                          !value.match(
                            /^https:\/\/search\.shopping\.naver\.com\/(?:catalog\/\d+|search\/all(?:[?#]|$))/,
                          )
                        ) {
                          return "유효한 네이버 URL이 아닙니다.";
                        } else if (
                          currentCompareSite === "danawa" &&
                          !value.match(
                            /^https:\/\/prod\.danawa\.com\/info\/\?pcode=\d+/,
                          )
                        ) {
                          return `유효한 다나와 URL이 아닙니다. ${currentCompareSite}`;
                        } else if (
                          currentCompareSite === "enuri" &&
                          !value.match(
                            /^https:\/\/(?:price\.enuri\.com\/catalog\/\d+\/?(?:[?#]|$)|www\.enuri\.com\/detail\.jsp\?modelno=\d+(?:[&#]|$))/,
                          )
                        ) {
                          return "유효한 에누리 URL이 아닙니다.";
                        }
                        return true;
                      },
                      pattern: {
                        value: /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(:\d+)?(\/[^\s]*)?(\?[^\s]*)?$/,
                        message: "유효한 URL을 입력하세요.",
                      },
                    })}
                  />
                  {errors.compare_url && (
                    <div className="invalid-feedback">
                      {errors.compare_url.message}
                    </div>
                  )}
                  {errors.compare_column && (
                    <div className="invalid-feedback">
                      {errors.compare_column.message}
                    </div>
                  )}
                  {errors.compare_pages && (
                    <div className="invalid-feedback">
                      {errors.compare_pages.message}
                    </div>
                  )}
                </div>
                <div className="col-auto">
                  <button
                    type="button"
                    data-testid="id_compare_range_custom_button"
                    className="btn btn-outline-primary ms-2"
                    style={{ marginTop: "0" }}
                    onClick={async () => {
                      if (compareSite !== "direct" && !(await trigger("compare_url"))) {
                        return;
                      } else {
                        setCompareItemModalShow(true);
                      }
                    }}
                  >
                    미리보기
                  </button>
                </div>
              </div>
            </li>

            <li className="mb-3">
              <label className={`form-check-label d-inline-flex align-items-center ${lockedIncludeShipping !== null ? "text-muted" : ""}`}>
                <input
                  type="checkbox"
                  className="form-check-input me-2 mt-0"
                  style={lockedIncludeShipping !== null ? { filter: "grayscale(1)", opacity: 0.65 } : undefined}
                  {...register("include_shipping_fee")}
                  checked={lockedIncludeShipping ?? Boolean(includeShippingFee)}
                  aria-disabled={lockedIncludeShipping !== null}
                  readOnly={lockedIncludeShipping !== null}
                  onChange={(event) => setValue(
                    "include_shipping_fee",
                    lockedIncludeShipping ?? event.target.checked,
                    { shouldDirty: lockedIncludeShipping === null },
                  )}
                />
                배송비 포함 (노출가·판매가 비교 시 배송비를 포함합니다)
              </label>
            </li>

            {compareSite === "naver" && (
              <li className="mb-3">
                <label className="form-check-label">
                  <input
                    type="checkbox"
                    className="form-check-input me-2"
                    {...register("extra_config.naver_ungroup_sellers")}
                  />
                  동일 판매처 묶어보기 해제
                </label>
              </li>
            )}

            {/* 비교 범위, 순위 기준, 타겟 순위 */}
            <li className="row g-3 mb-3">
              <div className="col-auto">
                <label className="form-label">가격비교 범위</label>
                <select
                  className="form-select"
                  disabled={compareSite === "direct"}
                  {...register("compare_range", {
                    required: "가격비교 범위를 선택하세요.",
                  })}
                >
                  <option value="items_all">전체상품</option>
                  <option
                    value="items_same_market"
                    disabled={isSameMarketCompareRangeDisabled}
                  >
                    동일마켓
                  </option>
                  <option
                    value="items_specified"
                    disabled={!isPremium}
                  >
                    지정상품{" "}
                    {!isPremium && "(프로 전용)"}
                  </option>
                </select>
                {isSameMarketCompareRangeDisabled && (
                  <div className="form-text text-small">동일마켓 지정불가</div>
                )}
              </div>
              <div className="col-auto" style={{ minWidth: "160px" }}>
                <label className="form-label">내 상품</label>
                {/*
                내 상품 지정
                ----
                - 가격비교 아이템 목록 내에서 내 상품을 찾을 수 있도록 지정해 둬야 함.
                - 내 상품이 아닌 걸 억지로 지정하는 경우. Price 모델 생성 시 에러 발생
                - 그 이유는 노출가 < 판매가+할인액 조건에 부합되지 않을 수 있기 때문
                - 따라서 억지로 지정하는 대신 내 상품 못찾아도 경쟁하는 옵션을 이용
                - userAdjustConfig?.adjust_no_exposed.is_enabled 를 그대로 쓰지말고
                  form state로 관리하면서, validate 식에서 체크

                */}
                <input
                  type="hidden"
                  {...register(myItemIdFieldName, {
                    validate: (value) => {
                      if (value && value.length > 0) {
                        if (getValues("compare_site") === "direct" && !isDirectCompareItemUrl(value, false)) {
                          return "내 상품은 일반 상품 상세 URL로 지정하세요.";
                        }
                        return true;
                      } else {
                        if (canTryAssignMyCompareItem && getValues("extra_config.try_assign_my_compare_item")) {
                          return true;
                        }
                        if (resolveAdjustConfig(
                          userAdjustConfig?.adjust_no_exposed,
                          getValues("adjust_no_exposed"),
                        )?.is_enabled) {
                          return true;
                        } else {
                          return "내 상품을 지정하세요.";
                        }
                      }
                    },
                  })}
                />
                <div className="d-flex gap-2">
                  {myCompareItemId ? (
                    <span className="pt-1" title={myCompareItem?.memo || undefined}>
                      지정됨{myCompareItem?.memo ? ` · ${myCompareItem.memo}` : ""}
                    </span>
                  ) : (
                    <div className="d-flex gap-1">
                      <span className="pt-1">미지정</span>

                      {compareSite !== "direct" && <button
                        className="btn btn-link btn-sm pt-1 text-decoration-underline"
                        type="button"
                        disabled={
                          compareSite === "direct" || !compareUrl ||
                          !compareUrl.trim()
                        }
                        onClick={() => setMyCompareItemIdByUrlModalShow(true)}
                      >
                        {/*
                        setMyCompareItemId 직접 지정하는 버튼
                        - compare_url이 유효하지 않으면 모달 띄우는 버튼 비활성화
                        - 모달의 모양은 ShopItemSearchModal 참조
                        - 모달의 파일명은 MyCompareItemIdByUrlModal로 하자.
                        - 모달의 제목은 "내 상품 URL로 지정"
                        - URL 입력란과 확인 버튼, 취소 버튼으로 구성
                        - 입력을 검증한 후
                          - 에러인 경우에만 다른 모달과 같이 에러 메시지를 띄우 성공한 경우
                          - 성공한 경우 my_compare_item_id를 지정하고 모달 닫기
                        - 모달을 띄워서 INPUT에 URL을 받고 URL에서 my_compare_item_id를 추출해서 지정하는 방식
                        - URL을 입력받은 후 compare_site 별로 정형화된 형식에 맞는지 체크 -> 없으면 에러, 있으면 다음 단계
                        - URL을 입력받은 후 compare_site 별로 정형화된 형식에 맞으면 my_compare_item_id가 있는지 체크 -> 없으면 에러, 있으면 지정
                        - compare_site별 비교 아이템의 정형화된 URL
                          - 네이버
                            - URL: https://cr.shopping.naver.com/adcr?x=w3B%2FtWuMrpWnaufBGsxI1f%2F%2F%2Fw%3D%3DshlzoK1RBg0zbkhK7Rqm3vHLFWuHXLbScUhaE3qPK%2B0AysrumRZN5eInjAg6%2BqOMY%2B0Eeo41k56EpHDEQNuk2x0Dpo7FctmbK5zXoRSns46cQpGQOxrtxo13tbG8te7q7JxfYBSrxU%2BHc8r%2BjyXxpORDfSDBvRkBad9fdnOvVrJGG2oZw583aX0jFQ5D6l3ZR7vLuggky0qh9xU0hwTs8z2WG41h9Y7LMjbJovNyksEOVuJUSsOjUCbbqONpP9Nq59ybR8dqzMpGAzDPjxDppmu6%2Ff8jIIZT1w1Y%2FbXZKrVpMCMh%2BBt4pY0YL1B30LLdPiyljCAfKQI5bmkPsPgsnlxEf9cOB2XC69wFLsIgbTveVvpDM1ob%2BuTP9TrZQBVYuR8liWf%2Fd7YMys2FlbBDzAMjmzd75UADPj4GfbPatSSKPWsyKfGb1LLbSevDl9rnm83YbWN5eTgkUeg4E5vCKEglENHHMduNcCG6dNTK%2FaEE9JS0e9gh3DcQgvZQBeIKIiDTfUcGpRvY2mDclp%2FyW8phdXWWaAVSuQbjqH2oYMPBWkbDrUFJ2hBG8QaB8WVzn&nvMid=89681728787&catId=50002133
                            - my_compare_item_id 추출 방법: URL의 query string에서 nvMid 파라미터 값 추출
                          - 다나와
                            - URL: https://prod.danawa.com/bridge/loadingBridge.html?cate1=149&cate2=363&cate3=390&cate4=0&pcode=14797730&cmpnyc=EE715&safe_trade=4&fee_type=T&link_pcode=F302302668&package=0&setpc=0&keyword=%EC%97%A1%EC%86%90+EB-L1060U&r=17772752446047
                            - my_compare_item_id 추출 방법: URL의 query string에서 cmpnyc 파라미터 값과 link_pcode 파라미터 값을 "_"로 연결 (위 예시에서는 "EE715_F302302668")
                          - 에누리
                            - URL: https://www.enuri.com/move/Redirect.jsp?cmd=move_link&vcode=536&modelno=50293175&pl_no=27611988027&cate=02110300&urltype=0&coupon=0&from=detail&showPrice=5000050&buycnt=1&referrer=&tempid=20260427163737186&memberid=
                            - my_compare_item_id 추출 방법: URL의 query string에서 pl_no 파라미터 값 추출
                        */}
                        {compareSite === "danawa" ? "URL로 지정" : null}
                        {compareSite === "enuri" ? "URL로 지정" : null}
                        {compareSite === "naver" ? "URL로 지정" : null}
                      </button>}
                    </div>
                  )}
                </div>
                {canTryAssignMyCompareItem && canShowInhouseFeatures(auth) && (
                  <label className="form-check-label d-block mt-2">
                    <input
                      type="checkbox"
                      className="form-check-input me-2"
                      {...register("extra_config.try_assign_my_compare_item")}
                    />
                    내 상품 자동 지정 시도 (실험)
                  </label>
                )}
                {errors[myItemIdFieldName] && (
                  <div className="invalid-feedback d-block">
                    {errors[myItemIdFieldName]?.message}
                  </div>
                )}
              </div>
              {compareRange == "items_specified" && (
                <div className="col-auto" style={{ minWidth: "160px" }}>
                  <label className="form-label">지정상품</label>
                  <div className="d-flex gap-2">
                    {specifiedItems.length > 0 ? (
                      <span className="pt-1">
                        {specifiedItems.length} 건 (제한 {specifiedItemLimit}건)
                      </span>
                    ) : (
                      <span className="pt-1">
                        미지정
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div className="col-auto">
                <label className="form-label">순위 기준</label>
                <select
                  className="form-select"
                  {...register("rank_price_basis", {
                    required: "순위 기준을 선택하세요.",
                  })}
                >
                  <option value="price_exposed">노출가</option>
                  <option value="price" disabled={!isPremium}>
                    판매가 {!isPremium && "(프로 전용)"}
                  </option>
                </select>
              </div>
              <div className="col-auto pe-0 me-0">
                <label className="form-label">타겟 순위 </label>
                <select
                  id="target_rank"
                  className={`form-select ${errors.target_rank ? "is-invalid" : ""}`}
                  defaultValue="1"
                  {...register("target_rank", {
                    required: "타겟 순위를 선택하세요.",
                  })}
                >
                  {compareRange === "items_specified" ? (
                    specifiedItems.length < 1 ? (
                      <option value="" disabled selected>
                        선택 불가
                      </option>
                    ) : (
                      [...Array(
                        Math.min(
                          specifiedItems.length,
                          maxTargetRank,
                        ),
                      )].map((_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {i + 1}위
                        </option>
                      ))
                    )
                  ) : (
                    [...Array(maxTargetRank)].map((_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {i + 1}위
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="col-auto">
                <div className="d-flex align-items-start gap-1">
                  <label className="form-label">차액 기준</label>
                  <button
                    type="button"
                    className="btn btn-link text-muted"
                    aria-label="차액 기준 설명"
                    aria-expanded={showGapPriceBasisHelp}
                    aria-controls="item-gap-price-basis-help"
                    onClick={() => setShowGapPriceBasisHelp((visible) => !visible)}
                  >
                    <CircleQuestionMark size={16} aria-hidden="true" />
                  </button>
                </div>
                <select
                  className="form-select w-auto"
                  {...register("gap_price_basis", {
                    required: "차액 기준을 선택하세요.",
                  })}
                >
                  <option value="price_exposed">노출가</option>
                  <option value="price">판매가</option>
                </select>
                <div
                  id="item-gap-price-basis-help"
                  className={`form-text${showGapPriceBasisHelp ? "" : " d-none"}`}
                >
                  노출가로 설정시 상호 노출가를 비교
                </div>
              </div>
            </li>

            <li className="row g-3 mb-3">
              <div className="col-auto">
                <label className="form-label">제외 가격</label>
                <div className="d-flex gap-3">
                  <div>
                    <div className="input-group">
                      <span className="input-group-text">미만</span>
                      <input
                        type="text"
                        id="id_compare_price_min"
                        className={`form-control ${errors.compare_price_min ? "is-invalid" : ""}`}
                        style={{ maxWidth: "160px" }}
                        {...register("compare_price_min", {
                          required: "최소값을 입력하세요.",
                          min: { value: 0, message: "0 이상이어야 합니다." },
                        })}
                      />
                      <span className="input-group-text">원</span>
                    </div>
                    {errors.compare_price_min && (
                      <div className="invalid-feedback d-block">
                        {errors.compare_price_min.message}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="input-group">
                      <span className="input-group-text">초과</span>
                      <input
                        type="text"
                        id="id_compare_price_max"
                        className={`form-control ${errors.compare_price_max ? "is-invalid" : ""}`}
                        style={{ maxWidth: "160px" }}
                        {...register("compare_price_max", {
                          required: "최대값을 입력하세요.",
                          min: { value: 0, message: "0 이상이어야 합니다." },
                        })}
                      />
                      <span className="input-group-text">원</span>
                    </div>
                    {errors.compare_price_max && (
                      <div className="invalid-feedback d-block">
                        {errors.compare_price_max.message}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-text">
                  {compareSite === "direct" ? "배송비 제외 실판매가 기준" : "비교사이트 노출가 기준"}, 0으로 설정 시 제한없음
                </div>
              </div>
            </li>

            {compareRange === "items_specified" && (
              <li className="mb-3">
                <label className="form-label">지정 상품</label>
                {specifiedItems.length === 0 ? (
                  <div className="form-text">지정된 상품이 없습니다.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm align-middle text-nowrap" style={{ fontSize: "0.875rem" }}>
                      <thead>
                        <tr>
                          <th>상품명</th>
                          <th>포탈가</th>
                        </tr>
                      </thead>
                      <tbody>
                        {specifiedItems.map((specifiedItem, index) => {
                          const itemUrl = specifiedItem.dest_url || specifiedItem.item_url || specifiedItem.url;

                          return (
                            <tr key={`${specifiedItem.item_id || "specified"}-${index}`}>
                              <td
                                className="text-start"
                                title={specifiedItem.item_title || ""}
                                style={{ maxWidth: "20rem" }}
                              >
                                {specifiedItem.item_title ? (
                                  itemUrl ? (
                                    <a
                                      href={itemUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="d-inline-block text-truncate align-middle"
                                      style={{ maxWidth: "20rem" }}
                                    >
                                      {specifiedItem.item_title}
                                    </a>
                                  ) : (
                                    <span className="d-inline-block text-truncate align-middle" style={{ maxWidth: "20rem" }}>
                                      {specifiedItem.item_title}
                                    </span>
                                  )
                                ) : "-"}
                                {specifiedItem.memo && (
                                  <small className="d-block text-muted text-truncate" title={specifiedItem.memo}>
                                    {specifiedItem.memo}
                                  </small>
                                )}
                              </td>
                              <td>{formatSpecifiedPrice(specifiedItem.item_price)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </li>
            )}
            <li className="mb-3">
              <label className="form-label">경쟁상품 가격 스냅샷</label>
              <CompareItemPriceSnapshot snapshot={comparePriceSnapshot} />
            </li>
          </ul>
          </fieldset>

      <CompareItemSearchModal
        show={compareItemModalShow}
        onHide={async () => {
          setCompareItemModalShow(false);
        }}
      />

      <MyCompareItemIdByUrlModal
        show={myCompareItemIdByUrlModalShow}
        onHide={async () => {
          setMyCompareItemIdByUrlModalShow(false);
        }}
      />
    </>
  );
};

export default ItemComparisonFields;
